'use client';

import { Box, Typography, FormControl, Select } from '@mui/material';
import { cloneElement, useState } from 'react';
import { FilterAccordion, Hint } from '@/components';
import {
  StyledFormControl as RangeFormControl,
  StyledSlider,
  ValueWrapper,
  Separator,
  LabelBox,
  StyledRangeInput,
  HiddenMenuItem,
} from './styles';
import { formatNumberForDisplay } from '@/common/helpers';
import { useTranslations } from 'next-intl';
import { ArrowDown } from '@/components/icons/ArrowDown';
import { useRange } from './useRange';

interface RangeProps {
  label: string;
  value: number[];
  setValue: (value: number[]) => void;
  minValue: number;
  maxValue: number;
  step: number;
  isAccordion?: boolean;
  isSelect?: boolean;
  key?: string;
  disabled?: boolean;
  isPrice?: boolean;
  isArea?: boolean;
  width?: string;
  hideLabel?: boolean;
}

export const Range = ({
  label,
  value = [0, 0],
  setValue,
  minValue,
  maxValue,
  step,
  disabled,
  isAccordion = true,
  isSelect = false,
  isPrice = false,
  isArea = false,
  width,
  hideLabel = false,
}: RangeProps) => {
  const t = useTranslations();
  const [open, setOpen] = useState(false);

  const {
    innerValue,
    inputValue,
    handleFocus,
    handleBlur,
    handleInputChange,
    handleSliderChange,
    handleSliderCommit,
  } = useRange(value, minValue, maxValue, setValue);

  const renderRangeContent = () => (
    <Box onClick={(e) => e.stopPropagation()} sx={{ px: 1 }}>
      <RangeFormControl onClick={(e) => e.stopPropagation()}>
        <StyledSlider
          value={innerValue}
          step={step}
          min={minValue}
          max={maxValue}
          valueLabelDisplay="off"
          onChange={handleSliderChange}
          onChangeCommitted={handleSliderCommit}
          aria-label={label}
        />
        <ValueWrapper onClick={(e) => e.stopPropagation()}>
          <StyledRangeInput
            size="small"
            value={inputValue[0]}
            onChange={handleInputChange(0)}
            onFocus={handleFocus(0)}
            onBlur={handleBlur(0)}
            inputProps={{
              inputMode: 'numeric',
              pattern: '[0-9]*',
              'aria-label': `${label} minimum`,
            }}
          />
          <Separator style={{ width: '100%' }} />
          <StyledRangeInput
            size="small"
            value={inputValue[1]}
            onChange={handleInputChange(1)}
            onFocus={handleFocus(1)}
            onBlur={handleBlur(1)}
            inputProps={{
              inputMode: 'numeric',
              pattern: '[0-9]*',
              'aria-label': `${label} maximum`,
            }}
          />
        </ValueWrapper>
      </RangeFormControl>
    </Box>
  );

  if (isSelect) {
    const selectSerializedValue = JSON.stringify(innerValue);

    return (
      <FormControl size="small" sx={{ minWidth: width || '100px' }}>
        {!hideLabel && (
          <LabelBox>
            <span>{label}</span>
            <Hint message={t('searchResult.hints.maxValue')} />
          </LabelBox>
        )}
        <Select
          disabled={disabled}
          notched={hideLabel ? false : undefined}
          open={open}
          onOpen={() => setOpen(true)}
          onClose={() => setOpen(false)}
          IconComponent={(props) => <ArrowDown {...props} />}
          value={selectSerializedValue}
          label={hideLabel ? undefined : label + 'hn'}
          renderValue={() => {
            const formatVal = (v: number) =>
              step === 0.1
                ? Number(v).toLocaleString('de-DE', {
                    minimumFractionDigits: 1,
                    maximumFractionDigits: 1,
                  })
                : formatNumberForDisplay(v);
            return `${isPrice ? '€ ' : ''}${formatVal(innerValue[0])} – ${formatVal(innerValue[1])}${innerValue[1] === maxValue ? '+' : ''}${isArea ? ' m²' : ''}`;
          }}
          MenuProps={{
            disableScrollLock: true,
            PaperProps: {
              sx: {
                width: 200,
                padding: 1,
              },
            },
            disableAutoFocus: true,
            disableEnforceFocus: true,
            disableRestoreFocus: true,
          }}
        >
          {[
            <HiddenMenuItem
              key="__range_select_value__"
              dense
              disabled
              value={selectSerializedValue}
              tabIndex={-1}
              aria-hidden
            />,
            cloneElement(renderRangeContent(), { key: '__range_dropdown__' }),
          ]}
        </Select>
      </FormControl>
    );
  }

  if (isAccordion) {
    return (
      <FilterAccordion title={label} id={label} hint={t('searchResult.hints.maxValue')}>
        {renderRangeContent()}
      </FilterAccordion>
    );
  }

  return (
    <Box>
      <Box display="flex" gap="4px">
        <Typography fontWeight={700} fontSize={16} lineHeight="125%" mb={1}>
          {label}
        </Typography>
        <Hint message={t('searchResult.hints.maxValue')} />
      </Box>
      {renderRangeContent()}
    </Box>
  );
};
